import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import AdminLayout from "@/components/layout/AdminLayout";
import { useAuth } from "@/contexts/AuthContext";
import {
  useSuperusers,
  useSuperuserInvitations,
  type SuperuserRow,
} from "@/hooks/useSuperusers";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const formatDate = (iso: string): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

const fullName = (s: SuperuserRow): string => {
  const name = `${s.first_name ?? ""} ${s.last_name ?? ""}`.trim();
  return name || s.email || "Unknown";
};

export default function Settings() {
  const queryClient = useQueryClient();
  const { session } = useAuth();
  const userId = session?.user?.id ?? null;

  const {
    data: superusers = [],
    isLoading: superusersLoading,
    error: superusersError,
  } = useSuperusers();

  const {
    data: invitations = [],
    isLoading: invitesLoading,
    error: invitesError,
  } = useSuperuserInvitations();

  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      toast.error("Email is required");
      return;
    }
    if (!userId) {
      toast.error("You must be signed in to send an invitation");
      return;
    }
    setSubmitting(true);
    try {
      const expires_at = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
      const { error } = await supabase.from("superuser_invitations").insert({
        email: trimmed,
        invited_by: userId,
        status: "pending",
        expires_at,
      });
      if (error) throw error;
      toast.success(`Invitation sent to ${trimmed}`);
      setEmail("");
      queryClient.invalidateQueries({ queryKey: ["superusers"] });
      queryClient.invalidateQueries({ queryKey: ["superuser-invitations"] });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to send invitation";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async (id: string) => {
    setRevokingId(id);
    try {
      const { error } = await supabase
        .from("superuser_invitations")
        .update({ status: "cancelled" })
        .eq("id", id);
      if (error) throw error;
      toast.success("Invitation revoked");
      queryClient.invalidateQueries({ queryKey: ["superuser-invitations"] });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to revoke invitation";
      toast.error(msg);
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <AdminLayout title="Settings">
      <div className="max-w-4xl space-y-8">
        {/* Section 1: Platform Superusers */}
        <section className="bg-slate-800 border border-slate-700 rounded-lg p-6">
          <h2 className="text-base font-medium text-white mb-1">Platform Superusers</h2>
          <p className="text-sm text-slate-400 mb-4">
            Users with full platform-wide administrative access.
          </p>

          <div className="bg-slate-900 border border-slate-800 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-800 text-slate-400 uppercase text-xs">
                <tr>
                  <th className="text-left font-medium px-3 py-2">Name</th>
                  <th className="text-left font-medium px-3 py-2">Email</th>
                  <th className="text-left font-medium px-3 py-2">Role</th>
                  <th className="text-left font-medium px-3 py-2">Status</th>
                  <th className="text-left font-medium px-3 py-2">Added</th>
                </tr>
              </thead>
              <tbody>
                {superusersLoading && (
                  <>
                    {Array.from({ length: 3 }).map((_, i) => (
                      <tr key={i} className="border-b border-slate-800">
                        <td className="px-3 py-2.5"><Skeleton className="h-4 w-32 bg-slate-800" /></td>
                        <td className="px-3 py-2.5"><Skeleton className="h-4 w-48 bg-slate-800" /></td>
                        <td className="px-3 py-2.5"><Skeleton className="h-4 w-20 bg-slate-800" /></td>
                        <td className="px-3 py-2.5"><Skeleton className="h-4 w-16 bg-slate-800" /></td>
                        <td className="px-3 py-2.5"><Skeleton className="h-4 w-24 bg-slate-800" /></td>
                      </tr>
                    ))}
                  </>
                )}

                {!superusersLoading && superusersError && (
                  <tr>
                    <td colSpan={5} className="px-3 py-8 text-center text-sm text-red-400">
                      {(superusersError as Error).message}
                    </td>
                  </tr>
                )}

                {!superusersLoading && !superusersError && superusers.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-3 py-8 text-center text-sm text-slate-400">
                      No superusers found.
                    </td>
                  </tr>
                )}

                {!superusersLoading && !superusersError && superusers.map((s) => (
                  <tr key={s.id} className="border-b border-slate-800 hover:bg-slate-800/50">
                    <td className="px-3 py-2.5 font-semibold text-white">{fullName(s)}</td>
                    <td className="px-3 py-2.5 text-slate-400">{s.email || "—"}</td>
                    <td className="px-3 py-2.5">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-500/15 text-purple-300 border border-purple-500/30">
                        Superuser
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      {s.is_active ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-700/40 text-slate-400 border border-slate-600/40">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-slate-300">{formatDate(s.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-xs text-slate-500 mt-3">
            To remove a superuser, update the admin_users table directly in Supabase.
          </p>
        </section>

        {/* Section 2: Invite a New Superuser */}
        <section className="bg-slate-800 border border-slate-700 rounded-lg p-6">
          <h2 className="text-base font-medium text-white mb-1">Invite New Superuser</h2>
          <p className="text-sm text-slate-400 mb-4">
            Sends an invitation to the specified email address. The recipient must already have an account in the system.
          </p>

          <form onSubmit={handleInvite} className="flex items-end gap-3 max-w-xl">
            <div className="flex-1 space-y-1.5">
              <label htmlFor="invite-email" className="text-xs text-slate-300">
                Email address
              </label>
              <Input
                id="invite-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
                required
                disabled={submitting}
                className="bg-slate-950 border-slate-700 text-slate-100 placeholder:text-slate-500"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting || !email.trim()}
              className="bg-amber-500 hover:bg-amber-600 text-slate-950"
            >
              {submitting ? "Sending…" : "Send Invitation"}
            </Button>
          </form>

          {/* Pending invitations */}
          <div className="mt-6">
            <h3 className="text-sm font-medium text-white mb-2">Pending Invitations</h3>
            <div className="bg-slate-900 border border-slate-800 rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-800 text-slate-400 uppercase text-xs">
                  <tr>
                    <th className="text-left font-medium px-3 py-2">Email</th>
                    <th className="text-left font-medium px-3 py-2">Invited</th>
                    <th className="text-left font-medium px-3 py-2">Expires</th>
                    <th className="text-right font-medium px-3 py-2 w-24"></th>
                  </tr>
                </thead>
                <tbody>
                  {invitesLoading && (
                    <>
                      {Array.from({ length: 2 }).map((_, i) => (
                        <tr key={i} className="border-b border-slate-800">
                          <td className="px-3 py-2.5"><Skeleton className="h-4 w-48 bg-slate-800" /></td>
                          <td className="px-3 py-2.5"><Skeleton className="h-4 w-24 bg-slate-800" /></td>
                          <td className="px-3 py-2.5"><Skeleton className="h-4 w-24 bg-slate-800" /></td>
                          <td className="px-3 py-2.5"><Skeleton className="h-4 w-16 ml-auto bg-slate-800" /></td>
                        </tr>
                      ))}
                    </>
                  )}

                  {!invitesLoading && invitesError && (
                    <tr>
                      <td colSpan={4} className="px-3 py-8 text-center text-sm text-red-400">
                        {(invitesError as Error).message}
                      </td>
                    </tr>
                  )}

                  {!invitesLoading && !invitesError && invitations.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-3 py-8 text-center text-sm text-slate-400">
                        No pending invitations.
                      </td>
                    </tr>
                  )}

                  {!invitesLoading && !invitesError && invitations.map((inv) => (
                    <tr key={inv.id} className="border-b border-slate-800 hover:bg-slate-800/50">
                      <td className="px-3 py-2.5 text-white">{inv.email}</td>
                      <td className="px-3 py-2.5 text-slate-300">{formatDate(inv.created_at)}</td>
                      <td className="px-3 py-2.5 text-slate-300">{formatDate(inv.expires_at)}</td>
                      <td className="px-3 py-2.5 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={revokingId === inv.id}
                          onClick={() => handleRevoke(inv.id)}
                          className="h-7 px-2 text-xs border-slate-700 bg-slate-800 hover:bg-slate-700 text-red-400 hover:text-red-300"
                        >
                          {revokingId === inv.id ? "Revoking…" : "Revoke"}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>
    </AdminLayout>
  );
}
